import { ROLE_HIERARCHY } from "@medicorepay/shared";
import { useCallback, useMemo } from "react";
import { useAuthStore } from "../stores/authStore";

/**
 * Permission checking hook for granular access control
 *
 * @example
 * // Check single permission
 * const { hasPermission } = usePermissions();
 * if (hasPermission('products:create')) { ... }
 *
 * @example
 * // Check multiple permissions (OR)
 * const { hasAnyPermission } = usePermissions();
 * if (hasAnyPermission(['products:create', 'products:update'])) { ... }
 *
 * @example
 * // Check multiple permissions (AND)
 * const { hasAllPermissions } = usePermissions();
 * if (hasAllPermissions(['products:create', 'products:delete'])) { ... }
 */
export function usePermissions() {
  const user = useAuthStore((state) => state.user);
  const permissions = useMemo(
    () => user?.permissions ?? [],
    [user?.permissions],
  );
  const roleName = user?.roleName || user?.role;

  /**
   * Check if user has a specific permission
   * Supports direct match and wildcard matching (e.g., "billing:*")
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      // No user = no permission
      if (!user) return false;

      // SUPER_ADMIN has all permissions
      if (roleName === "SUPER_ADMIN") return true;

      // Check wildcard
      if (permissions.includes("*")) return true;

      // Direct match
      if (permissions.includes(permission)) return true;

      // Check group wildcard (e.g., "billing:*" matches "billing:create")
      const [group] = permission.split(":");
      if (permissions.includes(`${group}:*`)) return true;

      return false;
    },
    [user, permissions, roleName],
  );

  /**
   * Check if user has ANY of the specified permissions (OR logic)
   */
  const hasAnyPermission = useCallback(
    (permissionList: string[]): boolean => {
      return permissionList.some((perm) => hasPermission(perm));
    },
    [hasPermission],
  );

  /**
   * Check if user has ALL of the specified permissions (AND logic)
   */
  const hasAllPermissions = useCallback(
    (permissionList: string[]): boolean => {
      return permissionList.every((perm) => hasPermission(perm));
    },
    [hasPermission],
  );

  /**
   * Check if user has a specific role or higher in the hierarchy
   * Lower hierarchy number = higher privilege
   */
  const hasRole = useCallback(
    (role: string): boolean => {
      if (!roleName) return false;

      const userLevel = ROLE_HIERARCHY[roleName] ?? 100;
      const requiredLevel = ROLE_HIERARCHY[role] ?? 100;

      return userLevel <= requiredLevel;
    },
    [roleName],
  );

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = useCallback(
    (roles: string[]): boolean => {
      if (!roleName) return false;
      return roles.includes(roleName);
    },
    [roleName],
  );

  return {
    permissions,
    roleName,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    // Convenience checks for common operations
    canView: (resource: string) => hasPermission(`${resource}:view`),
    canCreate: (resource: string) => hasPermission(`${resource}:create`),
    canUpdate: (resource: string) => hasPermission(`${resource}:update`),
    canDelete: (resource: string) => hasPermission(`${resource}:delete`),
  };
}
